<template>
  <!-- Overlay para cerrar sidebar en mobile -->
  <div
    v-if="isMobile && drawer"
    class="fixed inset-0 bg-black/50 z-[999]"
    @click="closeSidebar"
  ></div>

  <aside
    :class="[
      isCollapsed ? 'w-16' : 'w-64',
      'border-r border-slate-200 dark:border-slate-800',
      'transition-all duration-300 ease-in-out flex flex-col h-full',
      'shadow-sm',
      isMobile ? 'fixed top-0 left-0 z-[1000] shadow-xl' : '',
      isMobile && !drawer ? '-translate-x-full' : 'translate-x-0'
    ]"
    :style="isMobile ? { backgroundColor: sidebarBgColor, height: '100dvh', maxHeight: '100dvh', overflow: 'hidden' } : { backgroundColor: sidebarBgColor }"
    role="navigation"
    aria-label="Menú principal"
  >
    <div class="flex flex-col h-full overflow-hidden">
      <!-- Parte fija: Logo y nombre -->
      <div class="flex-shrink-0 pt-4 px-3 pb-3">
        <div class="flex justify-center items-center mb-2">
          <AppLogo
            v-if="!isCollapsed"
            :alt="`Logo ${brand.nombre}`"
            class="w-40 h-auto cursor-pointer hover:opacity-80 transition-opacity duration-200"
            @click="$router.push('/inicio')"
          />

        </div>
        <div v-if="user && !isCollapsed" class="text-center">
          <p class="text-slate-600 dark:text-slate-400 text-sm font-bold truncate">
            {{ user.Nombres }}
          </p>
        </div>
      </div>
      <div class="flex-1 overflow-y-auto px-2 py-2 scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-700 scrollbar-track-transparent" @click="handleSidebarClick" :style="isMobile ? 'max-height: calc(100dvh - 220px);' : ''">

<!-- Botón de Inicio/Dashboard -->
<button
  @click.stop="goToDashboard"
  :aria-label="isCollapsed ? 'Ir al inicio' : undefined"
  :title="isCollapsed ? 'Inicio' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : ''"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-home text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Inicio</span>
  </span>
</button>

<!-- Botones del admin -->
<button
  v-if="user?.IdRol === 1"
  @click="handleClick(() => toggleReportesSubmenu())"
  :aria-expanded="showReportesSubmenu"
  :aria-label="isCollapsed ? 'Menú de reportes' : undefined"
  :title="isCollapsed ? 'Reportes' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-between'"
>
  <span class="flex items-center min-w-0" :class="isCollapsed ? '' : ''">
    <i class="bx bx-file text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Reportes</span>
  </span>
  <i
    v-if="!isCollapsed"
    class="bx bx-chevron-down text-base text-slate-400 transition-transform duration-200 flex-shrink-0"
    :class="{ 'rotate-180': showReportesSubmenu }"
  ></i>
</button>
<div
  v-if="showReportesSubmenu && !isCollapsed"
  class="ml-6 mb-1 space-y-0"
  role="menu"
>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('/reporte-pagos-dia')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Pagos registrados</span>
  </button>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('/propinas-restaurante')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Propina</span>
  </button>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('/historial-comandas')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Historial Comandas</span>
  </button>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('/historial-pagos-restaurante')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Historial Pagos Restaurante</span>
  </button>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('/historial-pagos-hotel')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Historial Pagos Hospedaje</span>
  </button>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('/reportes-hotel')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Habitaciones Ocupadas</span>
  </button>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('/reporte-camas')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Reporte de Camas</span>
  </button>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('ingresos-hospedaje')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Ingreso Hotel</span>
  </button>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('/reportes-restaurante')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Ingreso Restaurante</span>
  </button>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('/cuentas-pendientes-administracion')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Cuentas pendientes restaurante</span>
  </button>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('/cuentas-cargadas-habitacion')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Cuentas cargadas a habitación</span>
  </button>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('/reservas-pendientes-de-pago')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Reservas pendientes hospedaje</span>
  </button>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('/cierre-caja-fechas')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Cierre caja por fechas</span>
  </button>
  <button
    class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
    @click="$router.push('/cierre-caja-diario')"
    role="menuitem"
    style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  >
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Cierre de caja diario</span>
  </button>
</div>


<button
  v-if="user?.IdRol === 1"
  @click="toggleHotelSubmenu"
  :aria-expanded="showHotelSubmenu"
  :aria-label="isCollapsed ? 'Menú de hotel' : undefined"
  :title="isCollapsed ? 'Hotel' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-between'"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-hotel text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Hotel</span>
  </span>
  <i
    v-if="!isCollapsed"
    class="bx bx-chevron-down text-base text-slate-400 transition-transform duration-200 flex-shrink-0"
    :class="{ 'rotate-180': showHotelSubmenu }"
  ></i>
</button>
<div
  v-if="showHotelSubmenu && !isCollapsed"
  class="ml-6 mb-1 space-y-0"
  role="menu"
>
  <!--<button class="flex items-center w-full px-3 py-2 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-md transition-colors duration-150" @click="$router.push('/liberar-habitaciones')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-3"></span> Liberar Habitaciones
  </button> -->

  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" @click="$router.push('/reducir-reservas-admin')" role="menuitem" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Reducir/Cancelar Reservas</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" @click="$router.push('/gestion-empleados')" role="menuitem" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Empleados</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" @click="$router.push('/gestion-roles')" role="menuitem" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Roles</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" @click="$router.push('/listado-huesped')" role="menuitem" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Huespedes</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/pagos-empleados')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Pagos por cobrar a empleados</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/gestion-habitaciones')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Habitaciones</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/gestion-tipo-habitaciones')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Tipos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/gestion-tarifa-habitaciones')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Tarifas</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/servicios-extra')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Servicios Extra</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/control-mantenimiento')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Seguimiento Mantenimiento</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/gestion-mantenimiento')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Crear mantenimientos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/observaciones-limpieza')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Comentarios Limpieza</span>
  </button>
</div>


<!--
<button class="sidebar-btn" v-if="user?.IdRol === 1" @click="toggleInventarioSubmenu">
  <i class="bx bx-hotel"></i> Inventarios
  <i class="bx bx-chevron-down" style="margin-left: auto;" :class="{ 'rotated': showInventarioSubmenu }"></i>
</button>
<div v-if="showInventarioSubmenu" class="submenu">
  <button class="sidebar-btn sidebar-sub-btn" @click="$router.push('/inventario-tienda')">
    <i class="bx bx-chevron-right"></i> Tienda
  </button>
  <button class="sidebar-btn sidebar-sub-btn" @click="$router.push('/inventario-lavanderia')">
    <i class="bx bx-chevron-right"></i> Lavanderia
  </button>
</div>
-->


<button
  v-if="user?.IdRol === 1"
  @click="togglegastosgenerales"
  :aria-expanded="showgastosGenerales"
  :aria-label="isCollapsed ? 'Menú de gastos' : undefined"
  :title="isCollapsed ? 'Gastos' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-between'"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-folder text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Gastos</span>
  </span>
  <i
    v-if="!isCollapsed"
    class="bx bx-chevron-down text-base text-slate-400 transition-transform duration-200 flex-shrink-0"
    :class="{ 'rotate-180': showgastosGenerales }"
  ></i>
</button>
<div
  v-if="showgastosGenerales && !isCollapsed"
  class="ml-6 mb-1 space-y-0"
  role="menu"
>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/gastos-generales-hotel')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Hotel</span>
  </button>
</div>


<button
  v-if="user?.IdRol === 1"
  @click="toggleGeneralSubmenu"
  :aria-expanded="showGeneralSubmenu"
  :aria-label="isCollapsed ? 'Menú general' : undefined"
  :title="isCollapsed ? 'General' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-between'"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-cog text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">General</span>
  </span>
  <i
    v-if="!isCollapsed"
    class="bx bx-chevron-down text-base text-slate-400 transition-transform duration-200 flex-shrink-0"
    :class="{ 'rotate-180': showGeneralSubmenu }"
  ></i>
</button>
<div
  v-if="showGeneralSubmenu && !isCollapsed"
  class="ml-6 mb-1 space-y-0"
  role="menu"
>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/gestion-cuentas-bancarias')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Cuentas</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/extrasadmin')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-amber-500 mr-2 flex-shrink-0"></span>
    <span class="truncate font-semibold">⚙ Extras / Mantenimiento</span>
  </button>
</div>
<button
  v-if="user?.IdRol === 1 || user?.IdRol === 10"
  @click="$router.push('/crear-codigos-descuento')"
  :aria-label="isCollapsed ? 'Cupones' : undefined"
  :title="isCollapsed ? 'Cupones' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-purchase-tag text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Cupones</span>
</button>
<button
  v-if="user?.IdRol === 1"
  @click="$router.push('/caja-chica')"
  :aria-label="isCollapsed ? 'Caja chica Hotel' : undefined"
  :title="isCollapsed ? 'Caja chica Hotel' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-money text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Caja chica Hotel</span>
</button>
<button
  v-if="user?.IdRol === 1"
  @click="$router.push('/contabilidad/caja-chica-admin')"
  :aria-label="isCollapsed ? 'Caja Chica Administración' : undefined"
  :title="isCollapsed ? 'Caja Chica Administración' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-wallet text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Caja Chica Administración</span>
</button>
<button
  v-if="user?.IdRol === 1"
  @click="$router.push('/contabilidad/no-facturado')"
  :aria-label="isCollapsed ? 'No Facturado' : undefined"
  :title="isCollapsed ? 'No Facturado' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-receipt text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">No Facturado</span>
</button>
<button
  v-if="user?.IdRol === 1"
  @click="$router.push('/contabilidad/facturas-anuladas')"
  :aria-label="isCollapsed ? 'Facturas Anuladas' : undefined"
  :title="isCollapsed ? 'Facturas Anuladas' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-block text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Facturas Anuladas</span>
</button>
<button
  v-if="user?.IdRol === 1"
  @click="$router.push('/contabilidad/pagos-credito')"
  :aria-label="isCollapsed ? 'Pagos por Crédito' : undefined"
  :title="isCollapsed ? 'Pagos por Crédito' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-credit-card text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Pagos por Crédito</span>
</button>

         <!-- Botones del rol recepcionista -->
<!-- 1. Reservas -->
<button
  v-if="user?.IdRol === 2"
  @click="toggleReservaSubmenu"
  :aria-expanded="showReservaSubmenu"
  :aria-label="isCollapsed ? 'Menú de reservas' : undefined"
  :title="isCollapsed ? 'Reservas' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-between'"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-hotel text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Reservas</span>
  </span>
  <i
    v-if="!isCollapsed"
    class="bx bx-chevron-down text-base text-slate-400 transition-transform duration-200 flex-shrink-0"
    :class="{ 'rotate-180': showReservaSubmenu }"
  ></i>
</button>
<div
  v-if="showReservaSubmenu && !isCollapsed"
  class="ml-6 mb-1 space-y-0"
  role="menu"
>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/crear-reserva')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Crear Reserva</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/reservas')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Ver Reservas</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/historial-pagos-hotel')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Historial pagos hospedaje</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/imprimir-cambios-habitacion')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Ticket Cambio de habitación</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/imprimir-reporte-limpieza')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Reporte Limpieza</span>
  </button>
</div>

<!-- 2. Restaurante (Cuentas, Historial) -->
<button
  v-if="user?.IdRol === 2"
  @click="toggleRestauranteRecepSubmenu"
  :aria-expanded="showRestauranteRecepSubmenu"
  :aria-label="isCollapsed ? 'Menú de restaurante' : undefined"
  :title="isCollapsed ? 'Restaurante' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-between'"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-restaurant text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Restaurante</span>
  </span>
  <i
    v-if="!isCollapsed"
    class="bx bx-chevron-down text-base text-slate-400 transition-transform duration-200 flex-shrink-0"
    :class="{ 'rotate-180': showRestauranteRecepSubmenu }"
  ></i>
</button>
<div
  v-if="showRestauranteRecepSubmenu && !isCollapsed"
  class="ml-6 mb-1 space-y-0"
  role="menu"
>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/cuentas-pendientes-mesas')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Cuentas por mesa</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/cuentas-cargadas-habitacion')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Cuentas cargadas a habitación</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/historial-pagos-restaurante')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Historial de Pagos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/historial-comandas')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Historial de Comandas</span>
  </button>
</div>

<!-- 4. Hotel -->
<button
  v-if="user?.IdRol === 2"
  @click="togglemenurecepcion"
  :aria-expanded="showmenuRecepcion"
  :aria-label="isCollapsed ? 'Menú de hotel' : undefined"
  :title="isCollapsed ? 'Hotel' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-between'"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-folder text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Hotel</span>
  </span>
  <i
    v-if="!isCollapsed"
    class="bx bx-chevron-down text-base text-slate-400 transition-transform duration-200 flex-shrink-0"
    :class="{ 'rotate-180': showmenuRecepcion }"
  ></i>
</button>
<div
  v-if="showmenuRecepcion && !isCollapsed"
  class="ml-6 mb-1 space-y-0"
  role="menu"
>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/priorizar-limpieza')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Priorizar Limpieza</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/notas-supervision')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Comentarios Supervision Limpieza</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/observaciones-limpieza')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Comentarios Limpieza</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/habitaciones-mantenimiento')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Poner habitación en mantenimiento</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/gestion-mantenimiento')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Crear Mantenimiento</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/listado-huesped')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Listado de Huéspedes</span>
  </button>
</div>

<!-- 5. Tienda -->
<button
  v-if="user?.IdRol === 2"
  @click="$router.push('/venta-tienda')"
  :aria-label="isCollapsed ? 'Tienda' : undefined"
  :title="isCollapsed ? 'Tienda' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-store text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Tienda</span>
</button>

<!-- 6. Cuentas (Depositos, Cierre Caja, Caja Chica) -->
<button
  v-if="user?.IdRol === 2"
  @click="toggleEconomiaSubmenu"
  :aria-expanded="showEconomiaSubmenu"
  :aria-label="isCollapsed ? 'Menú de cuentas' : undefined"
  :title="isCollapsed ? 'Cuentas' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-between'"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-wallet text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Cuentas</span>
  </span>
  <i
    v-if="!isCollapsed"
    class="bx bx-chevron-down text-base text-slate-400 transition-transform duration-200 flex-shrink-0"
    :class="{ 'rotate-180': showEconomiaSubmenu }"
  ></i>
</button>
<div
  v-if="showEconomiaSubmenu && !isCollapsed"
  class="ml-6 mb-1 space-y-0"
  role="menu"
>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/depositos-efectivo')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Depositos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/cierre-caja-diario')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Cierre Caja</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/caja-chica')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Caja Chica Hotel</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/contabilidad/pagos-credito')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Pagos por Crédito</span>
  </button>
</div>

<!-- 7. Servicios Extra -->
<button
  v-if="user?.IdRol === 2"
  @click="$router.push('/venta-servicios-extras')"
  :aria-label="isCollapsed ? 'Servicios Extra' : undefined"
  :title="isCollapsed ? 'Servicios Extra' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-store text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Servicios Extra</span>
</button>

<!-- 8. Lista de Espera -->
<button
  v-if="user?.IdRol === 2"
  @click="$router.push('/clientes-en-espera')"
  :aria-label="isCollapsed ? 'Lista de Espera' : undefined"
  :title="isCollapsed ? 'Lista de Espera' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-time-five text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Lista de Espera</span>
  </span>
  <span v-if="totalPendientes > 0 && !isCollapsed" class="px-2 py-1 ml-auto text-xs font-bold text-white bg-red-600 rounded-full flex-shrink-0">{{ totalPendientes }}</span>
</button>

<!--Botones del area de limpieza-->
<button
  v-if="user?.IdRol === 3"
  @click="$router.push('/limpieza-habitaciones')"
  :aria-label="isCollapsed ? 'Limpieza' : undefined"
  :title="isCollapsed ? 'Limpieza' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-bath text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Limpieza</span>
</button>
<button
  v-if="user?.IdRol === 3"
  @click="$router.push('/gestion-mantenimiento')"
  :aria-label="isCollapsed ? 'Crear Mantenimiento' : undefined"
  :title="isCollapsed ? 'Crear Manten.' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-door-open text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Crear Manten.</span>
</button>
<button
  v-if="user?.IdRol === 3"
  @click="$router.push('/notas-supervision')"
  :aria-label="isCollapsed ? 'Notas Supervision' : undefined"
  :title="isCollapsed ? 'Notas Supervision' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-door-open text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Notas Supervision</span>
</button>


<!-- Boton principal de Encargado de inventarios -->
<button
  v-if="user?.IdRol === 5"
  @click="toggleInventarios"
  :aria-expanded="mostrarInventariosHotel"
  :aria-label="isCollapsed ? 'Menú de inventarios hotel' : undefined"
  :title="isCollapsed ? 'Inventarios Hotel' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-between'"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-folder text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Inventarios Hotel</span>
  </span>
  <i
    v-if="!isCollapsed"
    class="bx bx-chevron-down text-base text-slate-400 transition-transform duration-200 flex-shrink-0"
    :class="{ 'rotate-180': mostrarInventariosHotel }"
  ></i>
</button>
<div
  v-if="mostrarInventariosHotel && !isCollapsed"
  class="ml-6 mb-1 space-y-0"
  role="menu"
>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/movimiento-inventarios-hotel')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Movimiento de productos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/salidas-inventarios-hotel')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Descontar productos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/compras-inventario-general-hotel')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Ingreso de Productos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/inventario-general-hotel')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Inventario General</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/inventario-recepcion')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Inventario Recepcion</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/inventario-lavanderia')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Inventario Lavanderia</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/inventario-sobrante')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Inventario Sobrante</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/inventario-global')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-blue-500 mr-2 flex-shrink-0"></span>
    <span class="truncate">Reporte Global</span>
  </button>
</div>

<!-- Inventarios Restaurante -->
<button
  v-if="user?.IdRol === 5"
  @click="toggleInventariosRestaurante"
  :aria-expanded="mostrarInventariosRestaurante"
  :aria-label="isCollapsed ? 'Menú de inventarios restaurante' : undefined"
  :title="isCollapsed ? 'Inventarios Restaurante' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-between'"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-folder text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Inventarios Restaurante</span>
  </span>
  <i
    v-if="!isCollapsed"
    class="bx bx-chevron-down text-base text-slate-400 transition-transform duration-200 flex-shrink-0"
    :class="{ 'rotate-180': mostrarInventariosRestaurante }"
  ></i>
</button>
<div
  v-if="mostrarInventariosRestaurante && !isCollapsed"
  class="ml-6 mb-1 space-y-0"
  role="menu"
>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/movimientos-inventarios-restaurante')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Movimiento de Productos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/salidas-inventarios-restaurante')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Descontar Productos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/compras-inventariogeneral-restaurante')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Ingreso de Productos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/inventario-general-restaurante')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Inventario General</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/inventario-cocina')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Inventario Cocina</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/inventario-barra')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Inventario Barra</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/inventario-mobiliario-restaurante')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Inventario Mobiliario</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/inventario-limpieza-restaurante')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Inventario Limpieza</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/tipos-producto-restaurante')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Tipos de Producto</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/historial-comandas')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Historial Comandas</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/inventario-global')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-blue-500 mr-2 flex-shrink-0"></span>
    <span class="truncate">Reporte Global</span>
  </button>
</div>

<!-- Platillos Restaurante -->
<button
  v-if="user?.IdRol === 5"
  @click="togglePlatillos"
  :aria-expanded="mostrarPlatillos"
  :aria-label="isCollapsed ? 'Menú de platillos' : undefined"
  :title="isCollapsed ? 'Platillos' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-between'"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-food-menu text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Platillos</span>
  </span>
  <i
    v-if="!isCollapsed"
    class="bx bx-chevron-down text-base text-slate-400 transition-transform duration-200 flex-shrink-0"
    :class="{ 'rotate-180': mostrarPlatillos }"
  ></i>
</button>
<div
  v-if="mostrarPlatillos && !isCollapsed"
  class="ml-6 mb-1 space-y-0"
  role="menu"
>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/platillos-restaurante')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Gestión de Platillos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/grupos-opciones-guarniciones')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Grupos de complementos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/opciones-guarniciones')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Opciones de complementos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/categorias-platillos')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Categorías</span>
  </button>
</div>


<button
  v-if="user?.IdRol === 5"
  @click="$router.push('/empaque-platillos')"
  :aria-label="isCollapsed ? 'Empaque de platillos' : undefined"
  :title="isCollapsed ? 'Empaque de platillos' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-food-menu text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Empaque de platillos</span>
</button>

<button
  v-if="user?.IdRol === 5"
  @click="$router.push('/mesas')"
  :aria-label="isCollapsed ? 'Mesas' : undefined"
  :title="isCollapsed ? 'Mesas' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-chair text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Mesas</span>
</button>

<button
  v-if="user?.IdRol === 5"
  @click="$router.push('/venta-tienda')"
  :aria-label="isCollapsed ? 'Tienda' : undefined"
  :title="isCollapsed ? 'Tienda' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-store text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Tienda</span>
</button>

<button
  v-if="user?.IdRol === 5"
  @click="$router.push('/inventario-tienda')"
  :aria-label="isCollapsed ? 'Inventario Tienda' : undefined"
  :title="isCollapsed ? 'Inventario Tienda' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-folder text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Inventario Tienda</span>
</button>


<!--Botones del encargado de mantenimiento-->
<button
  v-if="user?.IdRol === 4"
  @click="$router.push('/control-mantenimiento')"
  :aria-label="isCollapsed ? 'Tareas de mantenimiento' : undefined"
  :title="isCollapsed ? 'Tareas de mantenimiento' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-wrench text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Tareas de mantenimiento</span>
</button>
<!--Botones del encargado de supervisar las limpiezas-->
<button
  v-if="user?.IdRol === 6"
  @click="$router.push('/supervisar-limpieza')"
  :aria-label="isCollapsed ? 'Supervisar Limpieza' : undefined"
  :title="isCollapsed ? 'Supervisar Limpieza' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-task text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Supervisar Limpieza</span>
</button>




<button
  v-if="user?.IdRol === 7"
  @click="$router.push('/seleccionar-mesa')"
  :aria-label="isCollapsed ? 'Tomar Pedido' : undefined"
  :title="isCollapsed ? 'Tomar Pedido' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-task text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Tomar Pedido</span>
</button>

<!--Botones de cajero del restaurante-->
<button
  v-if="user?.IdRol === 8"
  @click="toggleRestauranteCajaSubmenu"
  :aria-expanded="showRestauranteCajaSubmenu"
  :aria-label="isCollapsed ? 'Menú de restaurante' : undefined"
  :title="isCollapsed ? 'Restaurante' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-between'"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-restaurant text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Restaurante</span>
  </span>
  <i
    v-if="!isCollapsed"
    class="bx bx-chevron-down text-base text-slate-400 transition-transform duration-200 flex-shrink-0"
    :class="{ 'rotate-180': showRestauranteCajaSubmenu }"
  ></i>
</button>
<div
  v-if="showRestauranteCajaSubmenu && !isCollapsed"
  class="ml-6 mb-1 space-y-0"
  role="menu"
>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/cuentas-pendientes-mesas')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Cuentas por mesa</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/cuentas-cargadas-habitacion')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Cuentas cargadas a habitación</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/historial-pagos-restaurante')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Historial de Pagos</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/historial-pagos-hotel')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Historial pagos hospedaje</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/historial-comandas')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Historial de Comandas</span>
  </button>
</div>

<!-- ── Botones del Contador (rol 11) ── -->
<button
  v-if="user?.IdRol === 11"
  @click="handleClick(() => toggleContabilidadSubmenu())"
  :aria-expanded="showContabilidadSubmenu"
  :aria-label="isCollapsed ? 'Menú de contabilidad' : undefined"
  :title="isCollapsed ? 'Contabilidad' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-between'"
>
  <span class="flex items-center min-w-0">
    <i class="bx bx-spreadsheet text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
    <span v-if="!isCollapsed" class="truncate">Contabilidad</span>
  </span>
  <i
    v-if="!isCollapsed"
    class="bx bx-chevron-down text-base text-slate-400 transition-transform duration-200 flex-shrink-0"
    :class="{ 'rotate-180': showContabilidadSubmenu }"
  ></i>
</button>
<div
  v-if="showContabilidadSubmenu && !isCollapsed"
  class="ml-6 mb-1 space-y-0"
  role="menu"
>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/contabilidad/ingresos-diarios')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Libro de Servicios Hotel</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/contabilidad/corte-ventas')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Corte de Ventas</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/contabilidad/liquidacion-tarjetas')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Liquidación Tarjetas</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/cierre-caja-fechas')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Cierre de Caja</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/ingresos-hospedaje')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Ingresos Hospedaje</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/reportes-restaurante')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Ingresos Restaurante</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/contabilidad/pagos-credito')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Pagos por Crédito</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/contabilidad/caja-chica-admin')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Caja Chica Administración</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/contabilidad/no-facturado')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">No Facturado</span>
  </button>
  <button class="flex items-center w-full px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150" style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;" @click="$router.push('/contabilidad/facturas-anuladas')" role="menuitem">
    <span class="w-1 h-1 rounded-full bg-slate-400 mr-2 flex-shrink-0"></span>
    <span class="truncate">Facturas Anuladas</span>
  </button>
</div>

<!--Botones de pantallas-->
<button
  v-if="user?.IdRol === 9"
  @click="$router.push('/pantalla-cocina')"
  :aria-label="isCollapsed ? 'Pantalla Cocina' : undefined"
  :title="isCollapsed ? 'Pantalla Cocina' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-book text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Pantalla Cocina</span>
</button>

<button
  v-if="user?.IdRol === 9"
  @click="$router.push('/pantalla-barra')"
  :aria-label="isCollapsed ? 'Pantalla Barra' : undefined"
  :title="isCollapsed ? 'Pantalla Barra' : undefined"
  class="flex items-center w-full px-3 py-2.5 mb-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors duration-150"
  style="border-left: 0; border-right: 0; border-top: 0; border-bottom: 1px solid #e5e7eb; background: transparent;"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="isCollapsed ? 'justify-center' : 'justify-start'"
>
  <i class="bx bx-book text-xl flex-shrink-0" :class="isCollapsed ? '' : 'mr-3'"></i>
  <span v-if="!isCollapsed" class="truncate">Pantalla Barra</span>
</button>

      </div>
      <!-- Parte fija: Botón colapsar y Cerrar sesión -->
      <div class="flex-shrink-0 border-t border-slate-200 dark:border-slate-800 mb-6">
        <!-- Botón colapsar/expandir -->
        <button
          v-if="!isMobile"
          @click="isCollapsed = !isCollapsed"
          :aria-label="isCollapsed ? 'Expandir menú' : 'Colapsar menú'"
          :title="isCollapsed ? 'Expandir' : 'Colapsar'"
          class="flex items-center w-full px-3 py-2.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150 border-0 justify-center mb-2"
          :style="{ backgroundColor: sidebarBgColor }"
        >
          <span v-if="!isCollapsed" class="text-sm font-medium mr-4">Esconder</span>
          <i :class="isCollapsed ? 'bx bx-chevron-right' : 'bx bx-chevron-left'" class="text-3xl"></i>
        </button>
        <!-- Cerrar sesión -->
        <button
          @click="showLogoutModal = true"
          :aria-label="isCollapsed ? 'Cerrar sesión' : undefined"
          :title="isCollapsed ? 'Cerrar Sesión' : undefined"
          class="flex justify-center items-center w-full px-3 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-slate-800 transition-colors duration-150 border-0"
          :style="{ backgroundColor: sidebarBgColor }"
        >
          <i class="bx bx-log-out text-lg" :class="isCollapsed ? '' : 'mr-2'"></i>
          <span v-if="!isCollapsed">Cerrar Sesión</span>
        </button>
      </div>
    </div>
  </aside>
  <!-- Modal de confirmación para cerrar sesión -->
  <div v-if="showLogoutModal" class="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40">
    <div class="rounded-lg shadow-xl p-0 w-full max-w-md bg-slate-50 dark:bg-slate-900">
      <div class="px-12 pt-10 pb-4 flex flex-col items-center">
        <i class="bx bx-log-out text-5xl text-red-500 mb-3"></i>
        <h2 class="text-xl font-bold mb-3 text-slate-800 dark:text-slate-200 text-center">¿Cerrar sesión?</h2>
        <p class="text-base text-slate-500 dark:text-slate-400 mb-6 text-center">Se cerrará tu sesión y volverás al inicio de sesión.</p>
      </div>
      <div class="flex justify-center gap-6 px-12 pb-8">
        <button class="px-7 py-2 rounded text-base font-medium text-slate-600 dark:text-slate-300 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 transition border-0" @click="showLogoutModal = false">Cancelar</button>
        <button class="px-7 py-2 rounded text-base font-medium text-white bg-red-500 hover:bg-red-600 transition border-0" @click="confirmLogout">Cerrar sesión</button>
      </div>
    </div>
  </div>
</template>

<script>
import AppLogo from '@/components/AppLogo.vue';
import { BRAND } from '@/config/branding';
export default {
  name: 'SidebarPrincipal',
  components: { AppLogo },
  emits: ['toggle-drawer', 'logout'],
  props: {
    drawer: Boolean,
    isMobile: Boolean,
    user: Object,
    totalPendientes: Number
  },
  data() {
    return {
      brand: BRAND,
      isCollapsed: false,
      showLogoutModal: false,
      showReservaSubmenu: false,
      showHotelSubmenu: false,
      showInventarioSubmenu: false,
      showReportesSubmenu: false,
      showGeneralSubmenu: false,
      mostrarInventariosHotel: false,
      mostrarInventariosRestaurante: false,
      showgastosGenerales: false,
      showmenuRecepcion: false,
      showRestauranteRecepSubmenu: false,
      showRestauranteCajaSubmenu: false,
      mostrarPlatillos: false,
      mostrarMeseroPlatillos: false,
      showEconomiaSubmenu: false,
      showContabilidadSubmenu: false,
    };
  },
  computed: {
    sidebarBgColor() {
      // Escuchar el estado de dark mode desde Vuex
      const isDark = this.$store?.getters?.isDarkMode || document.documentElement.classList.contains('dark');
      return isDark ? '#0f172a' : '#f8fafc'; // slate-900 : slate-50
    }
  },
  methods: {
    closeSidebar() {
      // Emitir evento para cerrar el drawer en mobile/tablet
      if (this.isMobile) {
        this.$emit('toggle-drawer');
      }
    },
    confirmLogout() {
      this.showLogoutModal = false;
      this.$emit('logout');
    },
    goToDashboard() {
      // Redirigir a inicio para todos los roles
      this.$router.push('/inicio');
      // Cerrar sidebar en mobile después de navegar
      this.closeSidebar();
    },
    handleSidebarClick(event) {
      // Si la sidebar está colapsada y se hace click en un botón
      if (this.isCollapsed) {
        const button = event.target.closest('button');
        if (button) {
          // Prevenir la acción del botón
          event.stopPropagation();
          event.preventDefault();
          // Expandir la sidebar
          this.isCollapsed = false;
          return;
        }
      }

      // Si es mobile y se hace click en un botón que no tiene submenú, cerrar sidebar
      if (this.isMobile) {
        const button = event.target.closest('button');
        if (button) {
          // Verificar si el botón tiene un submenú (tiene bx-chevron-down)
          const hasSubmenu = button.querySelector('.bx-chevron-down');
          // Si no tiene submenú, cerrar el sidebar después de un breve delay
          if (!hasSubmenu) {
            setTimeout(() => {
              this.closeSidebar();
            }, 200);
          }
        }
      }
    },
    handleClick(action) {
      if (this.isCollapsed) {
        // Si está colapsada, solo expandir
        this.isCollapsed = false;
        return;
      }
      // Si ya está expandida, ejecutar la acción
      if (typeof action === 'function') {
        action();
      }
    },
    toggleReservaSubmenu() {
      this.showReservaSubmenu = !this.showReservaSubmenu;
      if (this.showReservaSubmenu) this._closeOthers('showReservaSubmenu');
    },
    toggleInventarioSubmenu() {
      this.showInventarioSubmenu = !this.showInventarioSubmenu;
      if (this.showInventarioSubmenu) this._closeOthers('showInventarioSubmenu');
    },
    toggleHotelSubmenu() {
      this.showHotelSubmenu = !this.showHotelSubmenu;
      if (this.showHotelSubmenu) this._closeOthers('showHotelSubmenu');
    },
    toggleReportesSubmenu() {
      this.showReportesSubmenu = !this.showReportesSubmenu;
      if (this.showReportesSubmenu) this._closeOthers('showReportesSubmenu');
    },
    toggleGeneralSubmenu() {
      this.showGeneralSubmenu = !this.showGeneralSubmenu;
      if (this.showGeneralSubmenu) this._closeOthers('showGeneralSubmenu');
    },

    toggleInventarios() {
      this.mostrarInventariosHotel = !this.mostrarInventariosHotel;
      if (this.mostrarInventariosHotel) this._closeOthers('mostrarInventariosHotel');
    },

    toggleInventariosRestaurante() {
      this.mostrarInventariosRestaurante = !this.mostrarInventariosRestaurante;
      if (this.mostrarInventariosRestaurante) this._closeOthers('mostrarInventariosRestaurante');
    },

    togglegastosgenerales() {
      this.showgastosGenerales = !this.showgastosGenerales;
      if (this.showgastosGenerales) this._closeOthers('showgastosGenerales');
    },
    togglemenurecepcion() {
      this.showmenuRecepcion = !this.showmenuRecepcion;
      if (this.showmenuRecepcion) this._closeOthers('showmenuRecepcion');
    },
      togglePlatillos() {
    this.mostrarPlatillos = !this.mostrarPlatillos;
    if (this.mostrarPlatillos) this._closeOthers('mostrarPlatillos');
  },
  toggleMeseroPlatillos() {
    this.mostrarMeseroPlatillos = !this.mostrarMeseroPlatillos;
    if (this.mostrarMeseroPlatillos) this._closeOthers('mostrarMeseroPlatillos');
  },
    toggleEconomiaSubmenu() {
      this.showEconomiaSubmenu = !this.showEconomiaSubmenu;
      if (this.showEconomiaSubmenu) this._closeOthers('showEconomiaSubmenu');
    },
    toggleRestauranteRecepSubmenu() {
      this.showRestauranteRecepSubmenu = !this.showRestauranteRecepSubmenu;
      if (this.showRestauranteRecepSubmenu) this._closeOthers('showRestauranteRecepSubmenu');
    },
    toggleRestauranteCajaSubmenu() {
      this.showRestauranteCajaSubmenu = !this.showRestauranteCajaSubmenu;
      if (this.showRestauranteCajaSubmenu) this._closeOthers('showRestauranteCajaSubmenu');
    },
    toggleContabilidadSubmenu() {
      this.showContabilidadSubmenu = !this.showContabilidadSubmenu;
      if (this.showContabilidadSubmenu) this._closeOthers('showContabilidadSubmenu');
    },
    _closeOthers(keep) {
      const keys = [
        'showReservaSubmenu',
        'showHotelSubmenu',
        'showInventarioSubmenu',
        'showReportesSubmenu',
        'showGeneralSubmenu',
        'mostrarInventariosHotel',
        'mostrarInventariosRestaurante',
        'showgastosGenerales',
        'showmenuRecepcion',
        'showRestauranteRecepSubmenu',
        'showRestauranteCajaSubmenu',
        'mostrarPlatillos',
        'mostrarMeseroPlatillos',
        'showEconomiaSubmenu',
        'showContabilidadSubmenu',
      ];
      keys.forEach(k => { if (k !== keep) this[k] = false; });
    },
    handleMouseEnter(event) {
      const isDark = document.documentElement.classList.contains('dark');
      event.currentTarget.style.background = isDark ? '#1e293b' : '#f8fafc';
    },
    handleMouseLeave(event) {
      event.currentTarget.style.background = 'transparent';
    }
  }
}
</script>

<style scoped>

/* Scrollbar personalizado para navegadores que no soportan scrollbar-thin de Tailwind */
.scrollbar-thin::-webkit-scrollbar {
  width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background-color: #475569;
  border-radius: 3px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background-color: #64748b;
}

/* Firefox scrollbar */
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #475569 transparent;
}
</style>
